// Geteilte Konstanten und Helper rund um Aktivitäten.
// Werte (links) entsprechen den PocketBase-Select-Optionen,
// Labels (rechts) sind die deutschen Anzeigenamen.

export const TYP_OPTIONS = [
  { value: 'telefonat', label: 'Telefonat', icon: '📞' },
  { value: 'email', label: 'E-Mail', icon: '✉️' },
  { value: 'aufgabe', label: 'Aufgabe', icon: '✅' },
  { value: 'besuch', label: 'Besuch', icon: '🏥' },
  { value: 'notiz', label: 'Notiz', icon: '📝' },
];

export const TYP_LABEL = Object.fromEntries(TYP_OPTIONS.map((o) => [o.value, o.label]));
export const TYP_ICON = Object.fromEntries(TYP_OPTIONS.map((o) => [o.value, o.icon]));

export const STATUS_OPTIONS = [
  { value: 'offen', label: 'Offen' },
  { value: 'in_bearbeitung', label: 'In Bearbeitung' },
  { value: 'erledigt', label: 'Erledigt' },
];

export const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.value, o.label]));

// Klick auf Status-Pille schaltet zyklisch weiter.
export const STATUS_NEXT = {
  offen: 'in_bearbeitung',
  in_bearbeitung: 'erledigt',
  erledigt: 'offen',
};

export const STATUS_BADGE_CLASS = {
  offen: 'bg-amber-100 text-amber-800 ring-amber-200',
  in_bearbeitung: 'bg-primary-100 text-primary-800 ring-primary-200',
  erledigt: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
};

// Gesprächsergebnis (nur relevant bei typ = telefonat).
export const GESPRAECH_ERGEBNIS_OPTIONS = [
  { value: 'entscheider', label: 'Mit Entscheider gesprochen' },
  { value: 'nicht_entscheider', label: 'Mit Nicht-Entscheider gesprochen' },
  { value: 'nicht_erreicht', label: 'Niemanden erreicht' },
];

export const GESPRAECH_ERGEBNIS_LABEL = Object.fromEntries(
  GESPRAECH_ERGEBNIS_OPTIONS.map((o) => [o.value, o.label])
);

// Kurzlabels für die Tabellen-/Badge-Anzeige, wo der lange Satz zu viel wäre.
export const GESPRAECH_ERGEBNIS_SHORT_LABEL = {
  entscheider: 'Entscheider',
  nicht_entscheider: 'Nicht-Entscheider',
  nicht_erreicht: 'Nicht erreicht',
};

export const GESPRAECH_ERGEBNIS_BADGE_CLASS = {
  entscheider: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  nicht_entscheider: 'bg-amber-100 text-amber-800 ring-amber-200',
  nicht_erreicht: 'bg-slate-100 text-slate-700 ring-slate-200',
};

/** Formatiert ein PB-Datum (ISO oder leer) als deutsches Datum. */
export function formatDatum(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Kombiniert Datum + Uhrzeit zu "12.04.2026 14:30". Wenn keine Uhrzeit
 * gesetzt ist, wird nur das Datum zurückgegeben.
 */
export function formatDatumZeit(datum, uhrzeit) {
  const d = formatDatum(datum);
  if (d === '—') return d;
  if (!uhrzeit) return d;
  return `${d} ${uhrzeit}`;
}

/**
 * Vergleicher für Aktivitäten: erst nach Fälligkeitsdatum aufsteigend, dann
 * nach Uhrzeit aufsteigend. Aktivitäten OHNE Datum landen am Ende der Liste,
 * innerhalb eines Tages kommen Aufgaben mit Uhrzeit zuerst (chronologisch),
 * Aufgaben ohne Uhrzeit als Tagesabschluss.
 */
export function vergleicheAktivitaetenNachFaelligkeit(a, b) {
  const datumA = a.faellig_am || '9999-99-99';
  const datumB = b.faellig_am || '9999-99-99';
  if (datumA !== datumB) return datumA.localeCompare(datumB);
  const zeitA = a.uhrzeit || '99:99';
  const zeitB = b.uhrzeit || '99:99';
  return zeitA.localeCompare(zeitB);
}

/** Liefert true, wenn das Fälligkeitsdatum in der Vergangenheit liegt. */
export function istUeberfaellig(value, status) {
  if (!value || status === 'erledigt') return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const heute = new Date();
  heute.setHours(0, 0, 0, 0);
  return date < heute;
}

/**
 * PB liefert Datums-Felder als ISO-String. Für ein <input type="date">
 * brauchen wir das Format YYYY-MM-DD.
 */
export function toDateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

/**
 * Heute als Input-Value im YYYY-MM-DD Format — basierend auf der lokalen
 * Zeitzone. NICHT toISOString() verwenden, das würde nach 23 Uhr lokal
 * den nächsten Tag ausgeben (UTC-Verschiebung).
 */
export function heuteAlsInputValue() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Anzahl ganzer Tage zwischen heute und dem übergebenen Datum
 * (positiv wenn das Datum in der Vergangenheit liegt).
 * Liefert null, wenn kein gültiges Datum übergeben wurde.
 *
 * Wichtig: Math.round statt Math.floor, weil an DST-Umstellungstagen
 * 24-Stunden-Tage entweder 23 oder 25 Stunden lang sind und Math.floor
 * sonst um einen Tag verzählt.
 */
export function tageSeit(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const heute = new Date();
  heute.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  const diffMs = heute.getTime() - date.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

/** Anzeigename für einen PB-User-Record (oder Fallback). */
export function userAnzeige(user) {
  if (!user) return '—';
  const fullName = [user.vorname, user.nachname]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (fullName) return fullName;
  return user.name?.trim() || user.email || user.username || user.id;
}
